/**
 * What the universal in-platform file preview popup does (2026-08-01 directive: every deliverable type opens
 * user-friendly with per-type controls; 2026-08-02 amendments: opens for ALL file types every time, defaults
 * LARGE, sits above the fully-expanded chat surface, and can hand itself off to the app-level floating multitask
 * window via the Float control; lifted out of src/components/FilePreview/FilePreviewOverlay.tsx unchanged,
 * PV5-8:C part 1, 2026-10-13). The overlays draw what this answers.
 */
import React, { useEffect, useState } from 'react';
import { usePreview, openableHref, openInTab, type PreviewState } from './usePreview';
import { useFloatingPreview } from './floatingPreviews';
import type { PreviewOriginContext } from './previewOrigin';
import { useBodyScrollLock } from '../useBodyScrollLock';
import type { SerializedAttachment } from '../../components/GlobalChatSystem/types';
import { attachmentNoteOf } from '../../components/GlobalChatSystem/attachmentNote';
import { useIsChatDockedCompanion } from '../../components/GlobalChatSystem/dockedCompanion';
import { dockedWidthStyle, useDockedWidthChoice } from '../../components/GlobalChatSystem/dockedWidth';

export interface FilePreviewOverlayView {
  p: PreviewState;
  /** Prest0n's line saying what the file is (EFAX T16, 2026-10-09; attachmentNote.ts), under its name; '' when none. */
  note: string;
  wrap: boolean;
  bodyRef: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * The chat is on screen as the docked right rail, with room to share: the preview takes the remaining
   * screen (file on the left, conversation on the right, both live) instead of covering everything.
   */
  companion: boolean;
  /** The rail's width as the person keeps it, for the panel to clear (set on the panel itself). */
  companionStyle: React.CSSProperties | undefined;
  /** A text file has loaded: its copy and wrap tools show. */
  textTools: boolean;
  /** The kinds a full screen helps (a scene, a PDF, a page, a video). */
  canFullscreen: boolean;
  /** The app-level floating window layer is mounted: the Float control shows. */
  canFloat: boolean;
  /** Where "open in tab" goes (never a data: URL), else null and the control hides. */
  tabHref: string | null;
  canDownload: boolean;
  copyText: () => void;
  toggleWrap: () => void;
  fullscreen: () => void;
  floatOut: () => void;
  openTab: () => void;
  /** A press on the backdrop: contained, and closing only on the backdrop itself. */
  onBackdropClick: (e: React.MouseEvent) => void;
  portalTarget: HTMLElement;
}

export function useFilePreviewOverlay({ file, serialized, context, onClose }: {
  file?: File | null;
  serialized?: SerializedAttachment | null;
  context?: PreviewOriginContext | null;
  onClose: () => void;
}): FilePreviewOverlayView {
  const p = usePreview(file, serialized, context);
  const note = attachmentNoteOf(serialized);
  const [wrap, setWrap] = useState(true);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const floating = useFloatingPreview();

  // Is the chat on screen as the docked right rail, with room to share?
  // Read straight off the root-level stamp — no chat state, no provider, so
  // this works from the body portal on any page (dockedCompanion.ts).
  const companion = useIsChatDockedCompanion();
  // The panel clears the rail at the width the person keeps it; it holds that
  // variable itself, since it is not inherited (dockedWidth.ts).
  const dockedWidth = useDockedWidthChoice();

  // Page-scroll lock (2026-08-12 scrolling round): while this full-viewport
  // preview is up, wheel/touch past its edges must not scroll the page (or
  // chat surface) behind the backdrop. Reference-counted — stacks safely
  // with the fullscreen chat's own lock.
  //
  // NOT in companion mode (2026-08-24): there the preview is a panel beside
  // the chat, not a takeover — the page it shares the screen with is meant
  // to stay usable, scroll included. The lock is reference-counted, so
  // flipping this live releases/acquires cleanly as the chat docks.
  useBodyScrollLock(!companion);

  useEffect(() => {
    // Capture phase + stopPropagation: Escape closes THE PREVIEW ONLY — it must
    // never also reach the chat's own handlers (2026-08-02 report: closing the
    // preview collapsed the fully-extended thread).
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // openableHref, not url||blobUrl: window.open on a data: URL is a
  // silent no-op in Chrome/Firefox (review 2026-08-14).
  const tabHref = openableHref(p);

  return {
    p,
    note,
    wrap,
    bodyRef,
    companion,
    companionStyle: companion ? dockedWidthStyle(dockedWidth) : undefined,
    textTools: p.kind === 'text' && p.text != null,
    canFullscreen: p.kind === 'scene3d' || p.kind === 'pdf' || p.kind === 'html' || p.kind === 'video',
    canFloat: floating != null,
    tabHref,
    canDownload: Boolean(p.url || p.blobUrl),
    copyText: () => { navigator.clipboard?.writeText(p.text!).catch(() => undefined); },
    toggleWrap: () => setWrap((w) => !w),
    fullscreen: () => { bodyRef.current?.requestFullscreen?.().catch(() => undefined); },
    floatOut: () => {
      if (!floating) return;
      // Origin rides along (2026-08-24): a floated preview used to
      // lose its messageId — breaking the server's locator-free
      // resolve — and now also needs its thread so the window's own
      // "open chat" works days later, on another page.
      floating.openFloating({ kind: 'attachment', file, serialized, title: p.name, context });
      onClose();
    },
    openTab: () => { if (tabHref) openInTab(tabHref); },
    onBackdropClick: (e) => {
      // Portal events bubble the REACT tree into chat components — contain them.
      e.stopPropagation();
      if (e.target === e.currentTarget) onClose();
    },
    portalTarget: document.body,
  };
}
